import { Injectable, inject } from '@angular/core';
import { ChatAttachment } from '../../core/models/chat-message';
import { AuthService } from '../../core/services/auth.service';
import { Bill } from './bill-splitter.models';
import { BillSplitterCalculatorService } from './bill-splitter-calculator.service';

const BUCKET = 'bill-files';

export interface StoredBillSummary {
  id: string;
  merchant?: string;
  date?: string;
  currency: string;
  total: number;
  itemCount: number;
  people: string[];
  sourceFileName?: string;
  sourceMime?: string;
  sourcePath?: string;
  updatedAt: string;
}

type RemoteBill = {
  id: string;
  merchant: string | null;
  bill_date: string | null;
  currency: string;
  total_minor: number;
  item_count: number;
  source_file_name: string | null;
  source_mime: string | null;
  source_path: string | null;
  updated_at: string;
  bill?: unknown;
};

/** Persists user-owned bill results and their original source documents. */
@Injectable({ providedIn: 'root' })
export class BillStorageService {
  private readonly auth = inject(AuthService);
  private readonly calculator = inject(BillSplitterCalculatorService);

  async save(bill: Bill, attachment?: ChatAttachment): Promise<Bill> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) throw new Error('Sign in to save bills.');

    const source = await this.saveSource(user.id, bill, attachment);
    const persisted: Bill = { ...bill, ...(source ? { source } : {}) };
    const { error } = await client.from('bills').upsert({
      id: persisted.id,
      user_id: user.id,
      merchant: persisted.merchant ?? null,
      bill_date: persisted.date ?? null,
      currency: persisted.currency,
      total_minor: this.calculator.calculateTotal(persisted),
      item_count: persisted.items.length,
      source_path: persisted.source?.storagePath ?? null,
      source_file_name: persisted.source?.fileName ?? null,
      source_mime: persisted.source?.mime ?? null,
      bill: persisted,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(`Could not save this bill: ${error.message}`);
    return persisted;
  }

  async load(id: string): Promise<Bill | undefined> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) return undefined;
    const { data, error } = await client.from('bills').select('bill').eq('id', id).eq('user_id', user.id).maybeSingle();
    if (error) throw new Error(`Could not load this bill: ${error.message}`);
    return this.isBill(data?.bill) ? data.bill : undefined;
  }

  async list(): Promise<StoredBillSummary[]> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) return [];
    const { data, error } = await client.from('bills')
      .select('id,merchant,bill_date,currency,total_minor,item_count,source_file_name,source_mime,source_path,updated_at,bill')
      .eq('user_id', user.id)
      .order('bill_date', { ascending: false, nullsFirst: false })
      .order('updated_at', { ascending: false });
    if (error) throw new Error(`Could not load saved bills: ${error.message}`);
    return ((data ?? []) as RemoteBill[]).map((row) => ({
      id: row.id,
      merchant: row.merchant ?? undefined,
      date: row.bill_date ?? undefined,
      currency: row.currency,
      total: row.total_minor,
      itemCount: row.item_count,
      people: this.peopleFrom(row.bill),
      sourceFileName: row.source_file_name ?? undefined,
      sourceMime: row.source_mime ?? undefined,
      sourcePath: row.source_path ?? undefined,
      updatedAt: row.updated_at,
    }));
  }

  /** Deletes a saved bill and the private original file, when one was uploaded. */
  async delete(id: string, sourcePath?: string): Promise<void> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) throw new Error('Sign in to delete bills.');

    if (sourcePath) {
      if (!sourcePath.startsWith(`${user.id}/`)) throw new Error('The original file is unavailable.');
      const { error } = await client.storage.from(BUCKET).remove([sourcePath]);
      if (error) throw new Error(`Could not delete the original bill file: ${error.message}`);
    }

    const { error } = await client.from('bills').delete().eq('id', id).eq('user_id', user.id);
    if (error) throw new Error(`Could not delete this bill: ${error.message}`);
  }

  /** Produces a short-lived, authenticated URL for a user's private receipt file. */
  async sourceUrl(path: string): Promise<string> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user || !path.startsWith(`${user.id}/`)) throw new Error('The original file is unavailable.');
    const { data, error } = await client.storage.from(BUCKET).createSignedUrl(path, 60);
    if (error || !data?.signedUrl) throw new Error(`Could not open the original bill file: ${error?.message ?? 'No download URL returned.'}`);
    return data.signedUrl;
  }

  private async saveSource(userId: string, bill: Bill, attachment?: ChatAttachment): Promise<Bill['source'] | undefined> {
    const current = bill.source;
    if (!attachment || current?.storagePath) return current;
    const file = attachment.sourceFile ?? this.derivedFile(attachment);
    if (!file) return current;
    const safeName = (attachment.sourceFile?.name || attachment.name || 'receipt').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${userId}/${bill.id}/${safeName}`;
    const { error } = await this.auth.supabase!.storage.from(BUCKET).upload(path, file, {
      upsert: true,
      contentType: attachment.sourceFile?.type || attachment.mime || undefined,
    });
    if (error) throw new Error(`Could not store the original bill file: ${error.message}`);
    return { ...(current ?? { type: attachment.dataUrl ? 'image' : attachment.mime === 'application/pdf' ? 'pdf' : 'document' }), fileName: attachment.name, mime: attachment.mime, storagePath: path };
  }

  private derivedFile(attachment: ChatAttachment): Blob | undefined {
    if (attachment.dataUrl) {
      const match = attachment.dataUrl.match(/^data:([^;,]+)?(?:;base64)?,(.*)$/);
      if (!match) return undefined;
      const bytes = atob(match[2]);
      const values = Uint8Array.from(bytes, (character) => character.charCodeAt(0));
      return new Blob([values], { type: match[1] || attachment.mime });
    }
    return attachment.text ? new Blob([attachment.text], { type: attachment.mime || 'text/plain' }) : undefined;
  }

  private isBill(value: unknown): value is Bill {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const bill = value as Partial<Bill>;
    return typeof bill.id === 'string' && typeof bill.currency === 'string' && Array.isArray(bill.items) && Array.isArray(bill.people)
      && !!bill.tax && !!bill.serviceCharge && !!bill.tip && !!bill.discount;
  }

  private peopleFrom(value: unknown): string[] {
    if (!this.isBill(value)) return [];
    return value.people.map((person) => person.name.trim()).filter(Boolean);
  }
}
